import React, { useState, useEffect, useRef } from "react";
import { Box, Paper, Typography, FormControlLabel, Switch, Checkbox } from "@mui/material";
import { Button } from "../Button/Button";
import "./BudgetGrid.css";

interface BudgetGridProps {
  excelData: {
    data: any[][];
    columns: string[];
  } | null;
  budgetGrid?: boolean[][];
  apiResponse?: {
    status: string;
    budget_items?: Array<{
      row: string;
      description: string;
      cost: string;
    }>;
    description_column?: number;
    cost_column?: number;
  };
  maxRows?: number;
  originalRowIndices?: number[];
  originalColIndices?: number[];
  selectedRows?: number[];
  descriptionColumn?: number | null;
  costColumn?: number | null;
  onRowHeaderClick?: (rowIdx: number) => void;
  onColumnHeaderClick?: (colIdx: number, event: React.MouseEvent) => void;
  onCellValueChange?: (rowIdx: number, colIdx: number, value: string) => void;
  onSave?: (selectedRows: number[], descriptionColumn: number | null, costColumn: number | null, data: any[][]) => void;
  onSelectedRowsChange?: (selectedRows: number[]) => void;
  hideNonBudget?: boolean;
  isSaving?: boolean;
}

/**
 * BudgetGrid component to display a clickable HTML grid of budget data
 */
const BudgetGrid: React.FC<BudgetGridProps> = ({
  excelData,
  budgetGrid,
  apiResponse,
  maxRows,
  originalRowIndices,
  originalColIndices,
  selectedRows = [],
  descriptionColumn,
  costColumn,
  onRowHeaderClick,
  onColumnHeaderClick,
  onCellValueChange,
  onSave,
  onSelectedRowsChange,
  hideNonBudget = false,
  isSaving = false,
}) => {
  // State to track which cell is being edited
  const [editCell, setEditCell] = useState<{rowIdx: number, colIdx: number} | null>(null);
  const [editValue, setEditValue] = useState("");
  // Local copy of data for immediate updates
  const [localData, setLocalData] = useState<any[][] | null>(null);
  // Reference to the input element for auto-selection
  const inputRef = useRef<HTMLInputElement>(null);
  // State for hiding non-budget cells
  const [hideNonBudgetCells, setHideNonBudgetCells] = useState(false);

  // Determine if we're in "save mode" or "preview mode"
  const isSaveMode = onSave !== undefined && onRowHeaderClick !== undefined && onColumnHeaderClick !== undefined;

  // Update local data when excelData changes
  useEffect(() => {
    if (excelData && excelData.data) {
      setLocalData(JSON.parse(JSON.stringify(excelData.data)));
    }
  }, [excelData]);

  // Auto-deselect rows with invalid data (empty description OR empty/zero cost)
  useEffect(() => {
    if (!localData || selectedRows.length === 0) return;
    if (descriptionColumn === null && costColumn === null) return; // Need at least one column selected
    if (!onSelectedRowsChange) return; // Need callback to update selections
    
    // Filter out rows that have invalid data (missing valid description OR missing valid cost)
    const validRows = selectedRows.filter(rowIdx => {
      if (rowIdx < 0 || rowIdx >= localData.length) return false; // Invalid index
      
      const row = localData[rowIdx];
      if (!row) return false; // No row data
      
      // Check description column (if selected)
      let hasValidDescription = true; // Default to valid if column not selected
      if (descriptionColumn !== null && descriptionColumn !== undefined) {
        const description = String(row[descriptionColumn] || '').trim();
        // Description is invalid if empty or "[EMPTY]"
        hasValidDescription = description.length > 0 && description !== '[EMPTY]';
      }
      
      // Check cost column (if selected)
      let hasValidCost = true; // Default to valid if column not selected
      if (costColumn !== null && costColumn !== undefined) {
        const costValue = row[costColumn];
        const costStr = String(costValue || '').trim();
        // Cost is invalid if empty, "[EMPTY]", or zero
        if (!costStr || costStr === '[EMPTY]') {
          hasValidCost = false;
        } else {
          const numericCost = parseFloat(costStr.replace(/[$,\s]/g, ''));
          hasValidCost = !isNaN(numericCost) && numericCost !== 0;
        }
      }
      
      // Keep row only if it has BOTH valid description AND valid cost (for their respective selected columns)
      return hasValidDescription && hasValidCost;
    });
    
    // Update selection if invalid rows were found
    if (validRows.length !== selectedRows.length) {
      onSelectedRowsChange(validRows);
    }
  }, [localData, selectedRows, descriptionColumn, costColumn, onSelectedRowsChange]);

  // Effect to select all text in the input when editing starts
  useEffect(() => {
    if (editCell && inputRef.current) {
      // Select all text in the input when it's first focused
      inputRef.current.select();
    }
  }, [editCell]);

  if (
    !excelData ||
    !excelData.data ||
    !Array.isArray(excelData.data) ||
    !excelData.data.length
  ) {
    return (
      <Paper elevation={3} sx={{ p: 3, overflow: "hidden" }}>
        <Typography variant="h6" gutterBottom>
          📊 Analysis Results
        </Typography>
        <Typography variant="body1">
          No data available to display. Please select a file and analyze it.
        </Typography>
      </Paper>
    );
  }

  // Ensure budgetGrid is a valid array, even if empty
  const safeBudgetGrid = Array.isArray(budgetGrid) ? budgetGrid : [];

  const data = localData || excelData.data;
  const columns = excelData.columns || [];

  // Limit rows for display
  const displayRows = Math.min(maxRows || 500, data.length);
  const displayData = data.slice(0, displayRows);
  const displayBudget = safeBudgetGrid.slice(0, displayRows);

  // Use original indices if provided
  const rowIndices = originalRowIndices || [
    ...Array(displayData.length).keys(),
  ];
  const colIndices = originalColIndices || [...Array(columns.length).keys()];

  // Get API info
  const descriptionCol = apiResponse?.description_column;
  const costCol = apiResponse?.cost_column;

  // Functions for cell editing
  const handleDoubleClick = (rowIdx: number, colIdx: number, value: any) => {
    // Don't allow editing in preview mode
    if (!isSaveMode) return;
    
    // Don't allow editing row number cells
    if (colIdx === -1) return;

    // Convert [EMPTY] to empty string for editing
    const editableValue = value === "[EMPTY]" ? "" : value;

    // Start editing this cell
    setEditCell({ rowIdx, colIdx });
    setEditValue(String(editableValue || ""));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  const handleInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIdx: number,
    colIdx: number,
    originalRowIdx: number,
    originalColIdx: number
  ) => {
    if (e.key === "Enter") {
      // Update the local data for immediate visual feedback
      if (
        localData &&
        localData[rowIdx] &&
        localData[rowIdx][colIdx] !== undefined
      ) {
        const newData = JSON.parse(JSON.stringify(localData));
        newData[rowIdx][colIdx] = editValue;
        setLocalData(newData);
      }

      // Save the edit in the parent component
      if (onCellValueChange) {
        onCellValueChange(originalRowIdx, originalColIdx, editValue);
      }

      setEditCell(null);
    } else if (e.key === "Escape") {
      // Cancel the edit
      setEditCell(null);
    }
  };

  // Function to get CSS class for a cell
  const getCellClass = (rowIdx: number, colIdx: number, isBudget: boolean) => {
    const originalRowIdx = rowIndices[rowIdx];
    const originalColIdx = colIndices[colIdx];

    // Check if this row is selected by the user
    const isRowSelected = selectedRows.includes(originalRowIdx);

    // For selected rows, only description and cost columns should be green
    if (isRowSelected) {
      // Check if this cell is in a description or cost column
      const isDescriptionColumn =
        descriptionColumn !== null && originalColIdx === descriptionColumn;

      const isCostColumn = costColumn !== null && originalColIdx === costColumn;

      // API-detected columns only if user hasn't selected one
      const isApiDescriptionColumn =
        descriptionColumn === null &&
        descriptionCol !== null &&
        originalColIdx === descriptionCol;

      const isApiCostColumn =
        costColumn === null && costCol !== null && originalColIdx === costCol;

      // If this is a description or cost column in a selected row, make it green (budget)
      // Otherwise make it red (non-budget)
      if (isDescriptionColumn || isApiDescriptionColumn) {
        return "cell-budget description-cell";
      } else if (isCostColumn || isApiCostColumn) {
        return "cell-budget cost-cell";
      } else {
        return "cell-non-budget";
      }
    }

    // For unselected rows, always use non-budget class regardless of API classification
    // This ensures unselected rows show as gray even if API marked them as budget
    return "cell-non-budget";
  };

  // Function to get icon for a cell
  const getCellIcon = (rowIdx: number, colIdx: number, isBudget: boolean) => {
    const originalRowIdx = rowIndices[rowIdx];
    const originalColIdx = colIndices[colIdx];

    // Check if this row is selected by the user
    const isRowSelected = selectedRows.includes(originalRowIdx);

    // For selected rows
    if (isRowSelected) {
      // Check if this cell is in a description or cost column
      const isDescriptionColumn =
        descriptionColumn !== null && originalColIdx === descriptionColumn;

      const isCostColumn = costColumn !== null && originalColIdx === costColumn;

      // API-detected columns only if user hasn't selected one
      const isApiDescriptionColumn =
        descriptionColumn === null &&
        descriptionCol !== null &&
        originalColIdx === descriptionCol;

      const isApiCostColumn =
        costColumn === null && costCol !== null && originalColIdx === costCol;

      if (isDescriptionColumn || isApiDescriptionColumn) {
        return "✏️";
      } else if (isCostColumn || isApiCostColumn) {
        return "💰";
      } else {
        return ""; // Non-budget cells in selected rows
      }
    }

    // For unselected rows, don't show any budget icons
    return "";
  };

  const handleSave = () => {
    if (onSave && localData) {
      onSave(selectedRows, descriptionColumn ?? null, costColumn ?? null, localData);
    }
  };

  // Calculate the number of valid budget items (items with BOTH valid description AND valid cost)
  const getValidItemCount = (): number => {
    if (!localData || selectedRows.length === 0) return 0;
    
    return selectedRows.filter(rowIdx => {
      if (rowIdx < 0 || rowIdx >= localData.length) return false;
      
      const row = localData[rowIdx];
      if (!row) return false;
      
      // Check description column (if selected)
      let hasValidDescription = true; // Default to valid if column not selected
      if (descriptionColumn !== null && descriptionColumn !== undefined) {
        const description = String(row[descriptionColumn] || '').trim();
        // Description is invalid if empty or "[EMPTY]"
        hasValidDescription = description.length > 0 && description !== '[EMPTY]';
      }
      
      // Check cost column (if selected)
      let hasValidCost = true; // Default to valid if column not selected
      if (costColumn !== null && costColumn !== undefined) {
        const costValue = row[costColumn];
        const costStr = String(costValue || '').trim();
        // Cost is invalid if empty, "[EMPTY]", or zero
        if (!costStr || costStr === '[EMPTY]') {
          hasValidCost = false;
        } else {
          const numericCost = parseFloat(costStr.replace(/[$,\s]/g, ''));
          hasValidCost = !isNaN(numericCost) && numericCost !== 0;
        }
      }
      
      // Item is valid only if it has BOTH valid description AND valid cost (for their respective selected columns)
      return hasValidDescription && hasValidCost;
    }).length;
  };

  const validItemCount = getValidItemCount();

  // Calculate the total budget for selected items
  const getTotalBudget = (): number => {
    if (!localData || selectedRows.length === 0) return 0;
    if (costColumn === null && costCol === null) return 0;
    
    const effectiveCostColumn = costColumn ?? costCol;
    if (effectiveCostColumn === null || effectiveCostColumn === undefined) return 0;
    
    return selectedRows.reduce((total, rowIdx) => {
      if (rowIdx < 0 || rowIdx >= localData.length) return total;
      
      const row = localData[rowIdx];
      if (!row) return total;
      
      const costValue = row[effectiveCostColumn];
      const costStr = String(costValue || '').trim();
      
      if (!costStr || costStr === '[EMPTY]') return total;
      
      const numericCost = parseFloat(costStr.replace(/[$,\s]/g, ''));
      if (!isNaN(numericCost) && numericCost !== 0) {
        return total + numericCost;
      }
      
      return total;
    }, 0);
  };

  // Helper function to check if a row has valid description and cost
  const hasValidBudgetData = (row: any[], rowOriginalIdx: number): boolean => {
    if (!row) return false;
    
    // Determine which columns to check
    const descCol = descriptionColumn ?? descriptionCol ?? null;
    const cstCol = costColumn ?? costCol ?? null;
    
    // Need both columns to be defined
    if (descCol === null || cstCol === null) return false;
    
    // Check description
    const description = String(row[descCol] || '').trim();
    const hasValidDescription = description.length > 0 && description !== '[EMPTY]';
    
    // Check cost
    const costValue = row[cstCol];
    const costStr = String(costValue || '').trim();
    let hasValidCost = false;
    if (costStr && costStr !== '[EMPTY]') {
      const numericCost = parseFloat(costStr.replace(/[$,\s]/g, ''));
      hasValidCost = !isNaN(numericCost) && numericCost !== 0;
    }
    
    return hasValidDescription && hasValidCost;
  };

  // Filter columns when hideNonBudgetCells is enabled
  const getFilteredColumns = () => {
    if (!hideNonBudgetCells) {
      return { columns, colIndices };
    }
    
    const descCol = descriptionColumn ?? descriptionCol ?? null;
    const cstCol = costColumn ?? costCol ?? null;
    
    if (descCol === null && cstCol === null) {
      return { columns, colIndices };
    }
    
    const filteredIndices: number[] = [];
    const filteredColumns: string[] = [];
    
    colIndices.forEach((originalIdx, displayIdx) => {
      if (originalIdx === descCol || originalIdx === cstCol) {
        filteredIndices.push(originalIdx);
        filteredColumns.push(columns[displayIdx]);
      }
    });
    
    return { columns: filteredColumns, colIndices: filteredIndices };
  };

  // Filter rows when hideNonBudgetCells is enabled
  const getFilteredRows = () => {
    if (!hideNonBudgetCells) {
      return { displayData, rowIndices: rowIndices.slice(0, displayData.length) };
    }
    
    const filteredData: any[][] = [];
    const filteredIndices: number[] = [];
    
    displayData.forEach((row, displayIdx) => {
      const originalIdx = rowIndices[displayIdx];
      if (hasValidBudgetData(row, originalIdx)) {
        filteredData.push(row);
        filteredIndices.push(originalIdx);
      }
    });
    
    return { displayData: filteredData, rowIndices: filteredIndices };
  };

  const { columns: filteredColumns, colIndices: filteredColIndices } = getFilteredColumns();
  const { displayData: filteredDisplayData, rowIndices: filteredRowIndices } = getFilteredRows();

  // Handler for "Select All Visible Items" checkbox
  const handleSelectAllVisible = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectedRowsChange) return;
    
    if (event.target.checked) {
      // Select all visible rows (union with existing selections)
      const allVisibleRows = new Set([...selectedRows, ...filteredRowIndices]);
      onSelectedRowsChange(Array.from(allVisibleRows));
    } else {
      // Deselect all visible rows (remove visible rows from selections)
      const visibleRowsSet = new Set(filteredRowIndices);
      const remainingRows = selectedRows.filter(rowIdx => !visibleRowsSet.has(rowIdx));
      onSelectedRowsChange(remainingRows);
    }
  };

  // Determine if all visible items are selected
  const allVisibleSelected = filteredRowIndices.length > 0 && 
    filteredRowIndices.every(rowIdx => selectedRows.includes(rowIdx));
  
  // Determine if some (but not all) visible items are selected
  const someVisibleSelected = filteredRowIndices.length > 0 &&
    filteredRowIndices.some(rowIdx => selectedRows.includes(rowIdx)) &&
    !allVisibleSelected;

  return (
    <Paper elevation={0} sx={{ 
      p: 0, 
      overflow: "hidden", 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: 'none',
      border: 'none'
    }}>
      {isSaveMode && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0, gap: 2 }}>
          <div style={{ flex: 1, fontSize: '13px', color: '#666', lineHeight: '1.4' }}>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#333' }}>Instructions</h4>
              <ol>
                <li>Click header to select description column</li>
                <li>Shift+Click header to select cost column</li>
                <li>Click anywhere on a row to select/unselect it as a budget item</li>
                <li>Double-click any cell to edit its value, press Enter to save or Escape to cancel</li>
              </ol>
          </div>

          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, flexDirection: 'column' }}>
            <Button 
              onClick={handleSave}
              className="btn-primary"
              disabled={selectedRows.length === 0 || (descriptionColumn === null && costColumn === null) || isSaving}
              isLoading={isSaving}
              style={{ alignSelf: 'right' }}
            >
              Save Budget Items ({validItemCount})
            </Button>
            
            {selectedRows.length > 0 && (
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#333',
                  marginBottom: '-10px'
                }}
              >
                {validItemCount} Item{validItemCount !== 1 ? 's' : ''} Selected, Total Budget = ${getTotalBudget().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', padding: 0, margin: 0, gap: 0.5, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={handleSelectAllVisible}
                    color="primary"
                  />
                }
                label="Select All Visible Items"
                labelPlacement="start"
                sx={{ 
                  margin: 0,
                  padding: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px',
                    fontWeight: 500
                  }
                }}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={hideNonBudgetCells}
                    onChange={(e) => setHideNonBudgetCells(e.target.checked)}
                    color="primary"
                    edge="end"
                  />
                }
                label="Hide non-budget cells"
                labelPlacement="start"
                sx={{ 
                  margin: 0,
                    padding: 0,
                  marginRight: 0,
                  whiteSpace: 'nowrap',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {!isSaveMode && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <Typography variant="h6" sx={{ margin: 0 }}>
            📊 Sheet Preview
          </Typography>
        </Box>
      )}

      <Box sx={{ 
        overflow: "auto", 
        flex: 1,
        minHeight: 0
      }}>
        <table className="budget-grid">
          <thead>
            <tr>
              <th className="row-number"></th>
              {filteredColumns.map((col, idx) => {
                const originalColIdx = filteredColIndices[idx];
                let colClass = "";

                // Apply column highlighting
                if (
                  descriptionColumn !== null &&
                  originalColIdx === descriptionColumn
                ) {
                  colClass = "column-highlight"; // Just use column-highlight for manually selected columns
                } else if (
                  costColumn !== null &&
                  originalColIdx === costColumn
                ) {
                  colClass = "column-highlight"; // Just use column-highlight for manually selected columns
                } else if (
                  descriptionCol !== null &&
                  originalColIdx === descriptionCol &&
                  descriptionColumn === null // Only highlight API-detected columns if user hasn't selected one
                ) {
                  colClass = "column-highlight";
                } else if (
                  costCol !== null &&
                  originalColIdx === costCol &&
                  costColumn === null // Only highlight API-detected columns if user hasn't selected one
                ) {
                  colClass = "column-highlight";
                } else {
                  colClass = "column-number";
                }

                let colLabel = `${originalColIdx}`;
                let icon = null;
                if (
                  descriptionColumn !== null &&
                  originalColIdx === descriptionColumn
                ) {
                  icon = "✏️";
                  colLabel = "Description";
                } else if (
                  costColumn !== null &&
                  originalColIdx === costColumn
                ) {
                  icon = "💰";
                  colLabel = "Cost";
                } else if (
                  descriptionCol !== null &&
                  originalColIdx === descriptionCol &&
                  descriptionColumn === null // Only show icon for API-detected columns if user hasn't selected one
                ) {
                  icon = "✏️";
                } else if (
                  costCol !== null &&
                  originalColIdx === costCol &&
                  costColumn === null // Only show icon for API-detected columns if user hasn't selected one
                ) {
                  icon = "💰";
                }

                return (
                  <th
                    key={idx}
                    className={colClass}
                    onClick={(e) =>
                      isSaveMode && onColumnHeaderClick &&
                      onColumnHeaderClick(originalColIdx, e)
                    }
                    title={isSaveMode ? "Click to select as description column, Shift+Click to select as cost column" : "Column " + originalColIdx}
                    style={{ cursor: isSaveMode ? 'pointer' : 'default' }}
                  >
                    {icon && <span className="cell-icon">{icon}</span>}
                    {colLabel}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredDisplayData.map((row, rowIdx) => {
              const originalRowIdx = filteredRowIndices[rowIdx];

              return (
                <tr
                  key={rowIdx}
                  onClick={() =>
                    isSaveMode && onRowHeaderClick && onRowHeaderClick(originalRowIdx)
                  }
                  className={`${
                    selectedRows.includes(originalRowIdx) ? "row-selected" : ""
                  } ${isSaveMode ? "clickable-row" : ""}`}
                  style={{ cursor: isSaveMode ? 'pointer' : 'default' }}
                >
                  <td
                    className={`row-number ${
                      selectedRows.includes(originalRowIdx)
                        ? "row-selected"
                        : ""
                    }`}
                  >
                    {originalRowIdx}
                  </td>
                  {filteredColIndices.map((originalColIdx, colIdx) => {
                    // Get the actual cell value from the row using the original column index
                    const cell = row[originalColIdx];
                    
                    // Find the display index in the original (unfiltered) data for budget grid checking
                    const displayRowIdx = hideNonBudgetCells ? 
                      rowIndices.findIndex(idx => idx === originalRowIdx) : rowIdx;
                    const displayColIdx = hideNonBudgetCells ?
                      colIndices.findIndex(idx => idx === originalColIdx) : colIdx;
                    
                    // Add safety check for displayBudget
                    const isBudget = displayBudget?.[displayRowIdx]?.[displayColIdx] || false;
                    const cellClass = getCellClass(displayRowIdx, displayColIdx, isBudget);
                    const icon = getCellIcon(displayRowIdx, displayColIdx, isBudget);

                    // Truncate long values for display
                    const displayValue = String(cell || "");
                    // Replace [EMPTY] with empty string
                    const cleanedValue =
                      displayValue === "[EMPTY]" ? "" : displayValue;
                    const truncatedValue =
                      cleanedValue.length > 50
                        ? cleanedValue.substring(0, 27) + "..."
                        : cleanedValue;

                    const isEditing =
                      editCell &&
                      editCell.rowIdx === displayRowIdx &&
                      editCell.colIdx === displayColIdx;

                    return (
                      <td
                        key={colIdx}
                        className={cellClass}
                        onDoubleClick={() =>
                          handleDoubleClick(displayRowIdx, displayColIdx, cell)
                        }
                        title={`Row ${originalRowIdx}, Col ${originalColIdx}: ${cleanedValue}\nDouble-click to edit`}
                      >
                        {isEditing ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={handleInputChange}
                            onKeyDown={(e) =>
                              handleInputKeyDown(
                                e,
                                displayRowIdx,
                                displayColIdx,
                                originalRowIdx,
                                originalColIdx
                              )
                            }
                            autoFocus
                            className="cell-edit-input"
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                            onBlur={() => setEditCell(null)} // Cancel editing when clicking outside
                            ref={inputRef}
                          />
                        ) : (
                          <>
                            <span className="cell-icon">{icon}</span>
                            {truncatedValue}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      {/* {isSaveMode && (
        <Box textAlign="right" sx={{ mt: 1, p: 1, bgcolor: "#f8f9fa", border: 'none', boxShadow: 'none' }}>
          <Typography variant="body2" color="black"> 
            <strong>Legend:&nbsp;&nbsp;</strong>
            <span style={{ color: "#5e6cdc" }}>🔵 Blue = Selected Rows/Columns&nbsp;&nbsp;</span>
            <span style={{ color: "#228B22" }}>🟢 Green = Budget Items</span>
            <br />
            ✏️ = Description | 💰 = Cost
          </Typography>
        </Box>
      )} */}
    </Paper>
  );
};

export default BudgetGrid;